import React from 'react';
import { useAssessmentData } from './AssessmentDataContext';
import { SectionHeader, ScrollTable, BucketPill, DeltaBadge, MIN_RELIABLE_N, fmtPct, captureColor } from './shared';

// Extracted verbatim from Assessment.js's ExcursionTable component + its
// `activeTab === 'excursion'` render block (Tech Debt 2026-09, "Assessment
// and Backtest are god files" -- remaining-7-tabs context lift). Reads
// shared state from useAssessmentData() instead of via props.

function ExcursionTable({ buckets, prevByBucket = {} }) {
  return (
    <div>
      <p className="text-[12px] text-gray-500 mb-3 leading-relaxed">
        MAE = max adverse excursion from entry. MAE Win/Loss = MAE split by trade outcome.
        Cap = capture ratio (avg return ÷ avg MFE). Shakeout = WR(7d) − WR(60d).
      </p>
      <ScrollTable minWidth={640}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-20">Bucket</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-12">N</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[68px]">MAE 30d</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[64px]">MAE Win</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[64px]">MAE Loss</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[64px]">MFE 30d</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[56px]">Cap</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[64px]">Shakeout</th>
              <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[60px]">Recovery</th>
            </tr>
          </thead>
          <tbody>
          {buckets.map((row) => {
            const prev = prevByBucket[row.bucket];
            const lowN = row.sample_count < MIN_RELIABLE_N;
            const shakeout = lowN ? null : row.shakeout_depth;
            const prevShakeout = prev?.shakeout_depth;
            const shakeoutColor = shakeout == null ? 'text-gray-600'
              : shakeout < -5 ? 'text-trading-red-400'
              : shakeout < 0  ? 'text-amber-300'
              : 'text-gray-400';
            const cap = lowN ? null : row.capture_ratio?.['30d'];
            const prevCap = prev?.capture_ratio?.['30d'];
            return (
                <tr
                  key={row.bucket}
                  className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                >
                  <td className="py-2 px-3"><BucketPill bucket={row.bucket} /></td>
                  <td className="py-2 px-2 text-right text-gray-500 font-mono tabular-nums text-[11px] whitespace-nowrap">
                    {row.sample_count}
                    <DeltaBadge curr={row.sample_count} prev={prev?.sample_count} decimals={0} suffix="" />
                  </td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums text-[12px] text-trading-red-400 whitespace-nowrap">
                    {!lowN && row.avg_mae?.['30d'] != null ? fmtPct(row.avg_mae['30d']) : '—'}
                    <DeltaBadge curr={!lowN ? row.avg_mae?.['30d'] : null} prev={prev?.avg_mae?.['30d']} />
                  </td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums text-[12px] text-amber-300 whitespace-nowrap">
                    {!lowN && row.avg_mae_winner_30d != null ? fmtPct(row.avg_mae_winner_30d) : '—'}
                    <DeltaBadge curr={!lowN ? row.avg_mae_winner_30d : null} prev={prev?.avg_mae_winner_30d} />
                  </td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums text-[12px] text-trading-red-300 whitespace-nowrap">
                    {!lowN && row.avg_mae_loser_30d != null ? fmtPct(row.avg_mae_loser_30d) : '—'}
                    <DeltaBadge curr={!lowN ? row.avg_mae_loser_30d : null} prev={prev?.avg_mae_loser_30d} />
                  </td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums text-[12px] text-trading-green-400 whitespace-nowrap">
                    {!lowN && row.avg_mfe?.['30d'] != null ? fmtPct(row.avg_mfe['30d']) : '—'}
                    <DeltaBadge curr={!lowN ? row.avg_mfe?.['30d'] : null} prev={prev?.avg_mfe?.['30d']} />
                  </td>
                  <td className={`py-2 px-2 text-right font-mono tabular-nums text-[12px] whitespace-nowrap ${captureColor(cap)}`}>
                    {cap != null ? cap.toFixed(2) : '—'}
                    <DeltaBadge curr={cap} prev={prevCap} decimals={2} suffix="" />
                  </td>
                  <td className={`py-2 px-2 text-right font-mono tabular-nums text-[12px] whitespace-nowrap ${shakeoutColor}`}>
                    {shakeout != null ? fmtPct(shakeout) : '—'}
                    <DeltaBadge curr={shakeout} prev={prevShakeout} />
                  </td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums text-[12px] text-gray-500">
                    {row.shakeout_recovery != null ? `${row.shakeout_recovery}d` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollTable>
    </div>
  );
}


export default function ExcursionTab() {
  const { selectedWindowBuckets, prevByBucket, selectedWindow } = useAssessmentData();
  return (
  <>
    <SectionHeader>
      Excursion profile at 30d horizon
      {selectedWindow ? ` — ${selectedWindow} window` : ''}
    </SectionHeader>
    {selectedWindowBuckets.length === 0
      ? <p className="text-gray-500 text-sm text-center py-8">No buckets to display.</p>
      : <ExcursionTable buckets={selectedWindowBuckets} prevByBucket={prevByBucket} />
    }
  </>
  );
}
